import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, Fab, Zoom, useScrollTrigger, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyboardArrowUp, Store as PosIcon } from '@mui/icons-material';
import Header from './Header';
import Sidebar from './Sidebar';
import OnlineStatusIndicator from './OnlineStatusIndicator'; // ✅ NEW
import apiClient from '../../../api/client';
import { createDashboardTheme } from '../styles/dashboard-theme';
import DashboardContext from '../contexts/DashboardContext';
import '../styles/dashboard.css';

// Scroll to top button component
function ScrollToTopButton() {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{
          position: 'fixed',
          bottom: 96,
          right: 24,
          zIndex: 1,
        }}
      >
        <Fab 
          color="primary" 
          size="small"
          aria-label="scroll back to top"
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
            }
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      </Box>
    </Zoom>
  );
}

const MainLayout = ({ vendorData }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(vendorData || null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get theme state from context
  const { isDarkMode } = useContext(DashboardContext);

  // Memoize dynamic theme to prevent re-creation on every render
  const dynamicTheme = useMemo(() => 
    createDashboardTheme(isDarkMode ? 'dark' : 'light'), 
    [isDarkMode]
  );

  const MotionDiv = motion.div;

  // Memoize fetchVendorDetails to prevent unnecessary re-renders
  const fetchVendorDetails = useCallback(async () => {
    try {
      // Check if vendorData was passed as a prop
      if (vendorData) {
        setVendorDetails(vendorData);
        return;
      }
      
      // Try to get from localStorage first
      const storedVendorDetails = localStorage.getItem('vendorDetails');
      if (storedVendorDetails) {
        try {
          const parsed = JSON.parse(storedVendorDetails);
          setVendorDetails(parsed);
          return;
        } catch (e) {
          console.error('Error parsing stored vendor details:', e);
        }
      }
      
      // If not available, fetch from API
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await apiClient.get('/api/v1/users/vendor-details', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVendorDetails(response.data);
      localStorage.setItem('vendorDetails', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching vendor details', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/signin');
      }
    }
  }, [navigate, vendorData]);

  // Only fetch vendor details once on mount or when vendorData prop changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }

    if (!vendorDetails) {
      fetchVendorDetails();
    }
  }, [fetchVendorDetails, navigate, vendorDetails]);

  // Memoize drawer toggle to prevent re-renders
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  // Memoize POS click handler
  const handlePosClick = useCallback(() => {
    navigate('/dashboard/pos');
  }, [navigate]);

  const drawerWidth = 260;

  return (
    <ThemeProvider theme={dynamicTheme}>
      <Box 
        className="dashboard-container" 
        sx={{ 
          display: 'flex', 
          minHeight: '100vh',
          backgroundColor: 'background.default',
          color: 'text.primary',
          marginTop: '-90px', // Offset the body padding
          paddingTop: 0,
          transition: 'background-color 0.3s ease, color 0.3s ease'
        }}
      >
        <CssBaseline />
        
        {/* Header and Sidebar - These should NOT re-render on navigation */}
        <Header 
          handleDrawerToggle={handleDrawerToggle} 
          vendorDetails={vendorDetails}
        />
        
        <Sidebar 
          mobileOpen={mobileOpen} 
          handleDrawerToggle={handleDrawerToggle} 
        />
        
        {/* Main content area - Only this should change on navigation */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            maxWidth: '100%',
            overflowX: 'hidden',
            mt: 8, // Adjust for AppBar height
            transition: 'margin 0.2s ease',
            backgroundColor: 'background.default',
          }}
        >
          {/* Simplified animation - remove loading state that was causing issues */}
          <AnimatePresence mode="wait">
            <MotionDiv
              key={location.pathname} // This ensures animation triggers on route change
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              style={{ minHeight: 'calc(100vh - 104px)' }}
            >
              <Outlet />
            </MotionDiv>
          </AnimatePresence>
        </Box>
        
        {/* Floating POS Button */}
        <MotionDiv
          className="dashboard-pos-button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handlePosClick}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <PosIcon sx={{ fontSize: 28 }} />
        </MotionDiv>
        
        <ScrollToTopButton />
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;